import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { WhatsAppCTA } from "@/components/common/WhatsAppCTA";
import { useState } from "react";

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_9ddd47f9-cbc8-403c-b0f1-77e6c6e430be/artifacts/vi4rzx9p_Asset%201%404x.png";

const quickLinks = [
  { label: "Home", path: "/" },
  { label: "Flights", path: "/flights" },
  { label: "Tours", path: "/tours" },
  { label: "Cruises", path: "/cruises" },
  { label: "About Us", path: "/about" },
  { label: "How It Works", path: "/how-it-works" },
  { label: "Reviews", path: "/reviews" },
  { label: "Blog", path: "/blog" },
  { label: "Contact", path: "/contact" },
];

const serviceLinks = [
  { label: "International Flight Booking", path: "/international-flight-booking" },
  { label: "Flights to India from USA", path: "/flights-to-india-from-usa" },
  { label: "Flights to Africa from USA", path: "/flights-to-africa-from-usa" },
  { label: "Custom Tour Packages", path: "/custom-tour-packages" },
  { label: "Group Travel Planning", path: "/group-travel-planning" },
  { label: "Cruise Booking Assistance", path: "/cruise-booking-assistance" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState("");

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubscribeLoading(true);
    try {
      const response = await fetch("/api/newsletter-subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (data.success) {
        setConfirmationEmail(email);
        setShowConfirmation(true);
        setEmail("");
        setTimeout(() => setShowConfirmation(false), 4000);
      }
    } catch (error) {
      console.error("Newsletter subscribe error:", error);
    } finally {
      setSubscribeLoading(false);
    }
  };

  return (
    <footer className="bg-primary text-primary-foreground" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-5">
              <img src={LOGO_WHITE} alt="Tripsyfy" className="h-9 w-auto" />
            </Link>
            <p className="text-primary-foreground/70 text-sm leading-relaxed mb-5">
              Tripsyfy is a premium travel agency based in Edison, New Jersey, helping clients across the U.S. book expert-planned flights, tours, and cruises. We specialize in complex and international trips with transparent pricing and responsive support.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://instagram.com/tripsyfy"
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 rounded-full border border-primary-foreground/20 flex items-center justify-center text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground transition-colors"
                aria-label="Follow Tripsyfy on Instagram"
                data-testid="social-instagram"
              >
                <i className="fab fa-instagram text-sm"></i>
              </a>
              <a
                href="https://linkedin.com/company/tripsyfytravels"
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 rounded-full border border-primary-foreground/20 flex items-center justify-center text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground transition-colors"
                aria-label="Connect with Tripsyfy on LinkedIn"
                data-testid="social-linkedin"
              >
                <i className="fab fa-linkedin-in text-sm"></i>
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-base font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2.5">
              {quickLinks.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.path}
                    className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                    data-testid={`footer-link-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-base font-bold mb-4">Our Services</h3>
            <ul className="space-y-2.5">
              {serviceLinks.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.path}
                    className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                    data-testid={`footer-service-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-base font-bold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-accent shrink-0" />
                <a
                  href="tel:+19726661122"
                  className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                  data-testid="footer-phone"
                >
                  +1 972 666 1122
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-accent shrink-0" />
                <a
                  href="mailto:hello@tripsyfy.com"
                  className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                  data-testid="footer-email"
                >
                  hello@tripsyfy.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                <span className="text-sm text-primary-foreground/70">
                  Based in Edison, NJ — serving clients across the United States.
                </span>
              </li>
            </ul>
            <div className="mt-5 mb-4">
              <WhatsAppCTA
                variant="button"
                className="w-full justify-center"
              />
            </div>
            <div className="mt-2">
              <p className="text-sm text-primary-foreground/70 mb-2">Get fare alerts and trip insights</p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <label htmlFor="footer-newsletter-email" className="sr-only">Your email address</label>
                <Input
                  id="footer-newsletter-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="Enter your email"
                  aria-label="Email address for newsletter signup"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 text-sm h-9"
                  required
                />
                <Button
                  type="submit"
                  variant="accent"
                  size="icon"
                  className="shrink-0 h-9 w-9"
                  disabled={subscribeLoading}
                  aria-label="Subscribe"
                >
                  {subscribeLoading ? (
                    <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4" />
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>

        <div className="border-t border-primary-foreground/10 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-primary-foreground/50">
            &copy; {new Date().getFullYear()} Tripsyfy. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="text-xs text-primary-foreground/50 hover:text-primary-foreground/70 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-xs text-primary-foreground/50 hover:text-primary-foreground/70 transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>

      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full animate-in fade-in zoom-in duration-300">
            <div className="p-6 text-center">
              <div className="mb-4 flex justify-center">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center">
                  <Check className="w-8 h-8 text-accent stroke-3" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Welcome to Tripsyfy!</h3>
              <p className="text-sm text-muted-foreground mb-2">
                We've sent an exclusive welcome email to:
              </p>
              <p className="text-sm font-semibold text-primary mb-4 break-all">
                {confirmationEmail}
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Check your inbox for personalized travel deals, fare alerts, and curated packages from our travel experts.
              </p>
              <Button
                onClick={() => setShowConfirmation(false)}
                variant="default"
                className="w-full"
              >
                Got it!
              </Button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};
